import React, { useState, useRef, useEffect } from 'react';
import { Download, Printer, Save, MoveUp, MoveDown } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import invoiceService from '../../services/invoiceService';
import { toast } from '@/components/UI/use-toast';

// Utility Functions
const formatDate = (date) => {
  if (!date) return '';
  const options = {
    timeZone: 'Asia/Dubai',
    month: 'long',
    year: 'numeric',
  };
  const d = new Date(date);
  const dubaiDate = d.toLocaleString('en-US', { timeZone: 'Asia/Dubai' });
  const dubaiD = new Date(dubaiDate);

  return `${dubaiD.getDate()} ${new Intl.DateTimeFormat('en-US', options).format(d).toUpperCase()}`;
};

const formatNumber = (num) => {
  if (num === undefined || num === null) return '0.00';
  if (num === 'Na' || num === 'NA' || num === 'NaN') return 'NA';

  const value = parseFloat(num);
  if (isNaN(value)) return 'NA';
  if (value === 0) return '0.00';

  const isNegative = value < 0;
  const formattedNum = Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return isNegative ? `(${formattedNum})` : formattedNum;
};

const numberToWords = (number) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const scales = ['', 'Thousand', 'Million', 'Billion'];

  const convertGroup = (n, isLastGroup = false) => {
    let str = '';
    if (n >= 100) {
      str += ones[Math.floor(n / 100)] + ' Hundred';
      n %= 100;
      if (n > 0 && isLastGroup) str += ' and';
    }
    if (n >= 20) {
      str += (str && !str.endsWith('and') ? ' ' : ' ') + tens[Math.floor(n / 10)];
      if (n % 10 > 0) str += '-' + ones[n % 10];
    } else if (n >= 10) {
      str += (str && !str.endsWith('and') ? ' ' : ' ') + teens[n - 10];
    } else if (n > 0) {
      str += (str && !str.endsWith('and') ? ' ' : ' ') + ones[n];
    }
    return str.trim();
  };

  if (!number || isNaN(number)) return 'Zero Only.';
  if (number === 0) return 'Zero Only.';

  const num = Math.abs(number);
  const decimalPart = Math.round((num % 1) * 100);
  let str = '';
  let scale = 0;
  let remaining = Math.floor(num);

  while (remaining > 0) {
    const n = remaining % 1000;
    if (n !== 0) {
      const group = convertGroup(n, remaining < 1000);
      str = group + (scale > 0 ? ' ' + scales[scale] + ' ' : ' ') + str;
    }
    scale++;
    remaining = Math.floor(remaining / 1000);
  }

  str = str.trim();
  const decimalStr = decimalPart > 0 ? ` & ${decimalPart.toString().padStart(2, '0')}/100` : '';
  return str + (decimalStr || '') + ' Only.';
};

const InvoicePreview = ({ formData = {}, client = {}, project = {}, onBack }) => {
  const [isPreview, setIsPreview] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [manualAmounts, setManualAmounts] = useState({});
  const [forceUpdate, setForceUpdate] = useState(false);
  const hasOtherDeduction = formData.deductions?.otherDeduction?.checked;
  const invoiceRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeEditor, setActiveEditor] = useState(null);

  // Add state for custom row order
  const [customRowOrder, setCustomRowOrder] = useState([]);

  // Add print-specific styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
    @page {
      size: A4 portrait;
      margin: 0;
    }
    
    @media print {
      body * {
        visibility: hidden;
      }
      #invoice-content,
      #invoice-content * {
        visibility: visible;
      }
      #invoice-content {
        position: absolute;
        left: 0;
        top: 0;
        width: 210mm;
        height: 297mm;
        padding: 25mm 10mm 25mm 8mm;
      }
      
      #invoice-content table {
        border-collapse: collapse !important;
        width: 100% !important;
      }
      
      #invoice-content table,
      #invoice-content th,
      #invoice-content td {
        border: 1px solid black !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      
      #invoice-content th,
      #invoice-content td {
        padding: 2px 4px !important;
        position: relative !important;
        background-clip: padding-box !important;
      }
      
      #invoice-content tr {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      
      #invoice-content table,
      #invoice-content th,
      #invoice-content td {
        border-color: black !important;
        border-width: 1px !important;
        border-style: solid !important;
      }
      
      .text-red-600 { color: rgb(220, 38, 38) !important; }
      .text-blue-500, .text-blue-600 { color: rgb(59, 130, 246) !important; }
      .text-green-600 { color: rgb(16, 185, 129) !important; }
      .bg-yellow-300 { background-color: rgb(253, 224, 71) !important; }
      
      .row-controls, .print-hidden {
        display: none !important;
      }
    }

    .pdf-generation-mode {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      width: 210mm !important;
      height: 297mm !important;
      background: white !important;
      padding: 25mm 10mm 25mm 8mm !important;
      margin: 0 !important;
      box-sizing: border-box !important;
    }

    .pdf-generation-mode table {
      border-collapse: collapse !important;
      border-spacing: 0 !important;
      width: 100% !important;
    }

    .pdf-generation-mode td,
    .pdf-generation-mode th {
      border: 1px solid black !important;
      padding: 2px 4px !important;
      box-sizing: border-box !important;
    }

    @media screen {
      .row-controls {
        display: flex !important;
      }
      
      .print-hidden:not(.row-controls) {
        display: table-cell !important;
      }
    }
  `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Decimal Editor Component
  const DecimalEditor = ({ value, identifier, field, isTotal = false }) => {
    const editorId = `${identifier}-${field}`;
    const isActive = activeEditor === editorId;

    if (value === 'Na' || value === 'NA' || value === 'NaN' || value === null) {
      return <div className="text-center">NA</div>;
    }

    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return value;

    const actualValue = getAdjustedAmount(numericValue, identifier, field);
    if (actualValue === 0) return '0.00';

    const absValue = Math.abs(actualValue);
    const wholeNumber = Math.floor(absValue).toString();
    const decimals = absValue.toFixed(2).split('.')[1] || '00';

    const handleEditorClick = (e) => {
      e.stopPropagation();
      if (!isPreview) {
        setActiveEditor(isActive ? null : editorId);
      }
    };

    const handleInputChange = (newValue, isDecimal) => {
      handleDecimalEdit(identifier, field, newValue, isDecimal, isTotal);
    };

    const handleClickOutside = (e) => {
      if (!e.target.closest('.decimal-editor-popup')) {
        setActiveEditor(null);
      }
    };

    React.useEffect(() => {
      if (isActive) {
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
      }
    }, [isActive]);

    return (
      <div className="relative">
        <span
          className={`cursor-pointer ${!isPreview ? 'hover:bg-blue-50 px-1 rounded' : ''}`}
          onClick={handleEditorClick}
        >
          {formatNumber(actualValue)}
        </span>

        {!isPreview && isActive && (
          <div className="decimal-editor-popup absolute right-0 top-full mt-1 bg-white shadow-lg rounded border z-50 p-3 min-w-[200px]">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Edit Amount</span>
                <button
                  onClick={() => setActiveEditor(null)}
                  className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                >
                  ×
                </button>
              </div>

              <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                Current: {formatNumber(actualValue)}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Whole Number:
                </label>
                <input
                  type="number"
                  min="0"
                  value={wholeNumber}
                  onChange={(e) => handleInputChange(e.target.value, false)}
                  className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Enter full amount"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Decimals (00-99):
                </label>
                <input
                  type="number"
                  min="0"
                  max="99"
                  value={decimals}
                  onChange={(e) => handleInputChange(e.target.value, true)}
                  className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              <div className="flex space-x-2 pt-2">
                <button
                  onClick={() => {
                    const manualKey = `${identifier}-${field}`;
                    setManualAmounts(prev => {
                      const newAmounts = { ...prev };
                      delete newAmounts[manualKey];
                      return newAmounts;
                    });
                    setForceUpdate(prev => !prev);
                  }}
                  className="flex-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  Reset
                </button>
                <button
                  onClick={() => setActiveEditor(null)}
                  className="flex-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Handle decimal editing
  const handleDecimalEdit = (identifier, field, value, isDecimal = false, isTotal = false) => {
    if (value === '' || isNaN(value)) return;

    let newValue;
    let currentValue;

    if (isTotal) {
      const totals = calculateTotals(getOrderedRows());
      currentValue = totals[field];
    } else {
      const row = getOrderedRows()[identifier];
      if (!row) return;
      currentValue = parseFloat(row[field]);
    }

    if (isNaN(currentValue)) return;

    const manualKey = `${identifier}-${field}`;
    let existingValue = manualAmounts[manualKey] || currentValue;

    if (isDecimal) {
      const wholeNumber = Math.floor(Math.abs(existingValue));
      const decimalValue = Math.max(0, Math.min(99, parseInt(value) || 0));
      newValue = wholeNumber + (decimalValue / 100);
    } else {
      const wholeNumber = Math.max(0, parseInt(value) || 0);
      const currentDecimals = Math.round((Math.abs(existingValue) % 1) * 100);
      newValue = wholeNumber + (currentDecimals / 100);
    }

    // Deduction rows are stored negative; addition rows stay positive.
    const isDeduction = !isTotal && getOrderedRows()[identifier]?.isDeduction;
    if (isDeduction) newValue = -Math.abs(newValue);

    setManualAmounts(prev => ({
      ...prev,
      [manualKey]: newValue
    }));

    setForceUpdate(prev => !prev);
  };

  const getAdjustedAmount = (baseAmount, identifier, field) => {
    const manualKey = `${identifier}-${field}`;
    return manualAmounts[manualKey] ?? baseAmount;
  };

  const calculateRows = (formData) => {
    try {
      const rows = [];
      const cumulativeValue = parseFloat(formData.details?.cumulativeValue) || 0;
      const prevPayment = parseFloat(formData.details?.previousPayment) || 0;
      let currentSno = 1;

      const useCustomDescription = formData.details?.useCustomDescription;
      const customDescription = formData.details?.customDescription;
      const descriptionColor = formData.details?.descriptionColor || 'black';

      // ============================================================
      // Helper: push a line that is either DEDUCTED or ADDED based on `mode`.
      //   mode === 'add'    -> positive amounts, BLACK, "Add:" prefix, isAddition
      //   mode === 'deduct' -> negative amounts, RED,   "Less:" prefix, isDeduction
      // includeVat === false produces an NA / non-VAT line (total only).
      // ============================================================
      const pushLine = ({ body, amount, includeVat = true, mode = 'deduct', type, extra = {} }) => {
        const isAdd = mode === 'add';
        const sign = isAdd ? 1 : -1;
        const prefix = isAdd ? 'Add:' : 'Less:';

        if (includeVat) {
          const vatAmount = Number((amount * 0.05).toFixed(2));
          const totalAmount = Number((amount * 1.05).toFixed(2));
          rows.push({
            sno: currentSno++,
            description: `${prefix} ${body}`,
            qty: 1,
            unitPrice: sign * amount,
            taxable: sign * amount,
            vatPercentage: 5,
            vatAmount: sign * vatAmount,
            totalAmount: sign * totalAmount,
            isDeduction: !isAdd,
            isAddition: isAdd,
            type,
            deductionData: { amount, vatAmount, totalAmount, mode, ...extra }
          });
        } else {
          rows.push({
            sno: currentSno++,
            description: `${prefix} ${body}`,
            qty: 1,
            unitPrice: sign * amount,
            taxable: 'NA',
            vatPercentage: 'NA',
            vatAmount: 'NA',
            totalAmount: sign * amount,
            isDeduction: !isAdd,
            isAddition: isAdd,
            type,
            deductionData: { amount, mode, ...extra }
          });
        }
      };

      // Cumulative value description
      let description;
      if (useCustomDescription && customDescription) {
        description = `${customDescription} for Period Ending <strong>${formatDate(formData.details?.certificateDate)}</strong>, Based on Payment Certificate No.<strong>${formData.details?.paymentCertificateNo}</strong>`;
      } else {
        description = `Cumulative Value of Works Done + Variation works done for Period Ending <strong>${formatDate(formData.details?.certificateDate)}</strong>, Based on Payment Certificate No.<strong>${formData.details?.paymentCertificateNo}</strong>`;
      }

      rows.push({
        sno: currentSno++,
        description: description,
        qty: 1,
        unitPrice: cumulativeValue,
        taxable: cumulativeValue,
        vatPercentage: 5,
        vatAmount: Number((cumulativeValue * 0.05).toFixed(2)),
        totalAmount: Number((cumulativeValue * 1.05).toFixed(2)),
        isDeduction: false,
        type: 'cumulative',
        descriptionColor: descriptionColor
      });

      // Advance Release (Deduct or Add - user choice)
      if (formData.deductions?.advanceRelease?.checked) {
        const percentage = formData.deductions.advanceRelease.percentage;
        const hasFixedAmount = formData.deductions.advanceRelease.useFixedAmount;
        const useStoredAmount = formData.deductions.advanceRelease.useStoredAmount;

        let taxableAmount;
        if (hasFixedAmount && useStoredAmount && formData.deductions.advanceRelease.fixedAmount > 0) {
          taxableAmount = Number(parseFloat(formData.deductions.advanceRelease.fixedAmount).toFixed(2));
        } else {
          taxableAmount = Number((cumulativeValue * (percentage / 100)).toFixed(2));
        }

        pushLine({
          body: `${percentage}% Advance of Total Amount`,
          amount: taxableAmount,
          includeVat: true,
          mode: formData.deductions.advanceRelease.mode || 'deduct',
          type: 'advance_release',
          extra: { percentage, useFixedAmount: hasFixedAmount && useStoredAmount }
        });
      }

      // Retention Release (ALWAYS ADD - no toggle)
      if (formData.deductions?.retentionRelease?.checked) {
        const percentage = formData.deductions.retentionRelease.percentage;
        const taxableAmount = Number((cumulativeValue * (percentage / 100)).toFixed(2));

        pushLine({
          body: `${percentage}% Retention Release of Total Amount`,
          amount: taxableAmount,
          mode: 'add',
          type: 'retention_release',
          extra: { percentage }
        });
      }

      // Cumulative Retention (Deduct or Add - user choice)
      if (formData.deductions?.cumulativeretention?.checked) {
        const percentage = formData.deductions.cumulativeretention.percentage;
        const taxableAmount = Number((cumulativeValue * (percentage / 100)).toFixed(2));
        const retentionLabel = useCustomDescription && customDescription ? 'Retention' : 'Cumulative Retention';

        pushLine({
          body: `${percentage}% ${retentionLabel} of Total Amount`,
          amount: taxableAmount,
          mode: formData.deductions.cumulativeretention.mode || 'deduct',
          type: 'cumulative_retention',
          extra: { percentage }
        });
      }

      // NCR (Deduct or Add - user choice)
      if (formData.deductions?.ncrDeduction?.checked) {
        const taxableAmount = Number(parseFloat(formData.deductions.ncrDeduction.amount).toFixed(2)) || 0;
        const ncrMode = formData.deductions.ncrDeduction.mode || 'deduct';

        pushLine({
          body: ncrMode === 'add' ? 'NCR' : 'NCR Deduction',
          amount: taxableAmount,
          mode: ncrMode,
          type: 'ncr_deduction'
        });
      }

      // Previous Payments (always a deduction)
      if (prevPayment > 0) {
        const taxableAmount = prevPayment;
        const vatAmount = Number((taxableAmount * 0.05).toFixed(2));
        const totalAmount = Number((taxableAmount * 1.05).toFixed(2));

        rows.push({
          sno: currentSno++,
          description: 'Less: Previous Payments',
          qty: 1,
          unitPrice: -taxableAmount,
          taxable: -taxableAmount,
          vatPercentage: 5,
          vatAmount: -vatAmount,
          totalAmount: -totalAmount,
          isDeduction: true,
          type: 'previous_payment',
          deductionData: { taxableAmount, vatAmount, totalAmount }
        });
      }

      // Performance Retention (Deduct or Add - user choice)
      if (formData.deductions?.cumulativePerformanceRetention?.checked) {
        const percentage = formData.deductions.cumulativePerformanceRetention.percentage;
        const taxableAmount = Number((cumulativeValue * (percentage / 100)).toFixed(2));
        const performanceLabel = useCustomDescription && customDescription ? 'Performance Retention' : 'Cumulative Performance Retention';

        pushLine({
          body: `${percentage}% ${performanceLabel} of Total Amount`,
          amount: taxableAmount,
          mode: formData.deductions.cumulativePerformanceRetention.mode || 'deduct',
          type: 'performance_retention',
          extra: { percentage }
        });
      }

      // Other Deduction / Other Addition (Deduct or Add - user choice), with optional Sub Total
      if (formData.deductions?.otherDeduction?.checked) {
        const showSubtotal = formData.deductions.otherDeduction.showSubtotal;

        if (showSubtotal) {
          const regularTotals = rows.reduce((acc, row) => ({
            taxable: acc.taxable + (row.taxable === 'NA' ? 0 : (Number(row.taxable) || 0)),
            vatAmount: acc.vatAmount + (row.vatAmount === 'NA' ? 0 : (Number(row.vatAmount) || 0)),
            totalAmount: acc.totalAmount + (Number(row.totalAmount) || 0)
          }), { taxable: 0, vatAmount: 0, totalAmount: 0 });

          rows.push({
            sno: currentSno++,
            description: 'Sub Total',
            qty: 1,
            unitPrice: regularTotals.taxable,
            taxable: regularTotals.taxable,
            vatPercentage: 5,
            vatAmount: regularTotals.vatAmount,
            totalAmount: regularTotals.totalAmount,
            isSubtotal: true,
            type: 'subtotal'
          });
        }

        const amount = Number(parseFloat(formData.deductions.otherDeduction.amount).toFixed(2)) || 0;
        const includeVat = formData.deductions.otherDeduction.includeVat;
        const mode = formData.deductions.otherDeduction.mode || 'deduct';

        pushLine({
          body: mode === 'add' ? 'Other Additions' : 'Other Deductions',
          amount,
          includeVat,
          mode,
          type: 'other_deduction'
        });
      }

      return rows;
    } catch (error) {
      console.error('Error calculating rows:', error);
      return [];
    }
  };

  // Initialize customRowOrder when rows change
  useEffect(() => {
    const rows = calculateRows(formData);
    if (rows.length > 0 && customRowOrder.length === 0) {
      setCustomRowOrder(rows.map((_, index) => index));
    }
  }, [formData, customRowOrder.length]);

  // Function to get rows in custom order
  const getOrderedRows = () => {
    const originalRows = calculateRows(formData);

    if (customRowOrder.length === 0 || customRowOrder.length !== originalRows.length) {
      return originalRows;
    }

    return customRowOrder.map(index => {
      const row = originalRows[index];

      if (!row) {
        console.error(`Row at index ${index} is undefined. Original rows length: ${originalRows.length}`);
        return originalRows[0];
      }

      return row;
    });
  };

  // Function to move a row up or down
  const moveRow = (currentPosition, direction) => {
    const rows = calculateRows(formData);

    if (customRowOrder.length === 0 || customRowOrder.length !== rows.length) {
      const initialOrder = rows.map((_, idx) => idx);
      setCustomRowOrder(initialOrder);

      setTimeout(() => {
        moveRow(currentPosition, direction);
      }, 0);
      return;
    }

    const newOrder = [...customRowOrder];

    if (direction === 'up' && currentPosition > 0) {
      [newOrder[currentPosition], newOrder[currentPosition - 1]] =
        [newOrder[currentPosition - 1], newOrder[currentPosition]];
      setCustomRowOrder(newOrder);
    } else if (direction === 'down' && currentPosition < newOrder.length - 1) {
      [newOrder[currentPosition], newOrder[currentPosition + 1]] =
        [newOrder[currentPosition + 1], newOrder[currentPosition]];
      setCustomRowOrder(newOrder);
    }
  };

  // Totals = signed sum of every row except the Sub Total display row.
  const calculateTotals = (rows) => {
    if (!rows?.length) return { taxable: 0, vatAmount: 0, totalAmount: 0 };

    const calculable = rows.filter(row => !row.isSubtotal);

    const totals = calculable.reduce((acc, row) => ({
      taxable: acc.taxable + (row.taxable === 'NA' ? 0 : (Number(row.taxable) || 0)),
      vatAmount: acc.vatAmount + (row.vatAmount === 'NA' ? 0 : (Number(row.vatAmount) || 0)),
      totalAmount: acc.totalAmount + (Number(row.totalAmount) || 0)
    }), { taxable: 0, vatAmount: 0, totalAmount: 0 });

    return {
      taxable: Number(totals.taxable.toFixed(2)),
      vatAmount: Number(totals.vatAmount.toFixed(2)),
      totalAmount: Number(totals.totalAmount.toFixed(2))
    };
  };

  // Handle Print and PDF Generation
  const handleDownloadPDF = async () => {
    if (!invoiceRef.current) return;

    try {
      setIsGenerating(true);

      const elementsToHide = document.querySelectorAll('.print-hidden, .row-controls');
      elementsToHide.forEach(el => {
        el.style.display = 'none';
      });

      document.body.style.overflow = 'hidden';

      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.top = '0';
      tempContainer.style.left = '0';
      tempContainer.style.width = '210mm';
      tempContainer.style.height = '297mm';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.zIndex = '9999';
      tempContainer.style.padding = '25mm 10mm 25mm 8mm';
      tempContainer.style.boxSizing = 'border-box';

      const invoiceClone = invoiceRef.current.cloneNode(true);

      const controlsInClone = invoiceClone.querySelectorAll('.print-hidden, .row-controls');
      controlsInClone.forEach(el => el.remove());

      invoiceClone.style.width = '100%';
      invoiceClone.style.height = '100%';
      invoiceClone.style.padding = '0';
      invoiceClone.style.margin = '0';
      invoiceClone.style.backgroundColor = 'white';

      const tables = invoiceClone.querySelectorAll('table');
      tables.forEach(table => {
        table.style.borderCollapse = 'collapse';
        table.style.borderSpacing = '0';
        table.style.width = '100%';

        const cells = table.querySelectorAll('td, th');
        cells.forEach(cell => {
          cell.style.border = '1px solid black';
          cell.style.borderStyle = 'solid';
          cell.style.borderWidth = '1px';
          cell.style.borderColor = 'black';
          cell.style.padding = '2px 4px';
        });
      });

      tempContainer.appendChild(invoiceClone);
      document.body.appendChild(tempContainer);

      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 794,
        height: 1123,
        scrollX: 0,
        scrollY: 0,
        windowWidth: 794,
        windowHeight: 1123,
        logging: false,
        removeContainer: true,
        onclone: (clonedDoc) => {
          const style = clonedDoc.createElement('style');
          style.textContent = `
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            box-sizing: border-box !important;
          }
          
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }
          
          table {
            border-collapse: collapse !important;
            border-spacing: 0 !important;
            width: 100% !important;
          }
          
          td, th {
            border: 1px solid black !important;
            padding: 2px 4px !important;
            box-sizing: border-box !important;
          }
          
          .text-blue-500, .text-blue-600 { color: #2563EB !important; }
          .text-red-600 { color: #DC2626 !important; }
          .text-green-600 { color: #059669 !important; }
          .bg-yellow-300 { background-color: #FDE047 !important; }
          
          .print-hidden, .row-controls {
            display: none !important;
          }
        `;
          clonedDoc.head.appendChild(style);
        }
      });

      document.body.removeChild(tempContainer);

      elementsToHide.forEach(el => {
        el.style.display = '';
      });

      document.body.style.overflow = '';

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
        precision: 16
      });

      const imgData = canvas.toDataURL('image/png', 1.0);
      pdf.addImage(imgData, 'PNG', 0, 0, 210, 297, undefined, 'FAST');
      pdf.save(`Invoice-${formData.header?.serialNumber || 'download'}.pdf`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Error",
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);

      document.body.style.overflow = '';
      const tempContainers = document.querySelectorAll('[style*="z-index: 9999"]');
      tempContainers.forEach(container => {
        if (container.parentNode) {
          container.parentNode.removeChild(container);
        }
      });
    }
  };

  const handleSaveInvoice = async () => {
    if (!invoiceRef.current) return;

    try {
      setIsSaving(true);

      const isAliSons = client?.client_name?.includes("Ali & Sons");

      const styleSheet = document.createElement('style');
      styleSheet.textContent = `
          #invoice-content table {
              border-collapse: collapse !important;
              border-spacing: 0 !important;
          }
          #invoice-content td,
          #invoice-content th {
              border: 1px solid black !important;
              padding: 2px 4px !important;
              position: relative !important;
              background-clip: padding-box !important;
          }
          #invoice-content tr {
              break-inside: avoid !important;
          }
      `;
      document.head.appendChild(styleSheet);

      const controlButtons = invoiceRef.current.querySelectorAll('.row-controls');
      controlButtons.forEach(button => button.style.display = 'none');

      const canvas = await html2canvas(invoiceRef.current, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        windowWidth: invoiceRef.current.scrollWidth,
        windowHeight: invoiceRef.current.scrollHeight,
        logging: false
      });

      controlButtons.forEach(button => button.style.display = '');
      document.head.removeChild(styleSheet);

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
        precision: 16
      });

      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297, undefined, 'FAST');

      const pdfBlob = await pdf.output('blob');
      const pdfFile = new File([pdfBlob], `${formData.header.serialNumber}.pdf`, {
        type: 'application/pdf'
      });

      const rows = getOrderedRows();

      const typeKeyMap = {
        advance_release: 'advance',
        retention_release: 'retention',
        cumulative_retention: 'cumulative',
        performance_retention: 'performance',
        ncr_deduction: 'ncr',
        previous_payment: 'previous',
        other_deduction: 'other'
      };

      const deductionValues = {};
      rows.forEach(row => {
        const key = typeKeyMap[row.type];
        if (key) {
          deductionValues[key] = {
            taxable: row.taxable === 'NA' ? 0 : Math.abs(parseFloat(row.taxable)) || 0,
            vatAmount: row.vatAmount === 'NA' ? 0 : Math.abs(parseFloat(row.vatAmount)) || 0,
            totalAmount: Math.abs(parseFloat(row.totalAmount)) || 0
          };
        }
      });

      // Persist each deduction's mode. Retention Release is always 'add'.
      const deductionModes = {
        advanceRelease: formData.deductions.advanceRelease.mode || 'deduct',
        retentionRelease: 'add',
        cumulativeretention: formData.deductions.cumulativeretention.mode || 'deduct',
        cumulativePerformanceRetention: formData.deductions.cumulativePerformanceRetention.mode || 'deduct',
        ncrDeduction: formData.deductions.ncrDeduction.mode || 'deduct',
        otherDeduction: formData.deductions.otherDeduction.mode || 'deduct'
      };

      const certDate = new Date(formData.details.certificateDate || formData.header.taxInvoiceDate);
      const monthYear = `${certDate.toLocaleString('default', { month: 'short' })} ${certDate.getFullYear()}`;

      const metadata = {
        serial_number: formData.header.serialNumber,
        tax_invoice_ref: formData.header.taxInvoiceRef,
        tax_invoice_date: formData.header.taxInvoiceDate,

        client_name: client.client_name,
        project_code: project.project_code,
        project_name: project.project_name,
        loa_wo_sub_contract_ref: project.loa_wo_sub_contract_ref,
        amount: finalAmount,
        trn: client.trn || '',
        address: client.address || '',

        payment_certificate_no: formData.details.paymentCertificateNo || '',
        certificate_date: formData.details.certificateDate,
        cumulative_value: parseFloat(formData.details.cumulativeValue || 0),
        previous_payment: parseFloat(formData.details.previousPayment || 0),

        custom_description: formData.details.customDescription || '',
        description_color: formData.details.descriptionColor || 'black',
        custom_amount: parseFloat(formData.details.customAmount || 0),

        advance_release_checked: formData.deductions.advanceRelease.checked,
        advance_release_percentage: formData.deductions.advanceRelease.checked ?
          parseInt(formData.deductions.advanceRelease.percentage) : 0,
        advance_release_taxable: deductionValues.advance?.taxable || 0,
        advance_release_vat: deductionValues.advance?.vatAmount || 0,
        advance_release_total: deductionValues.advance?.totalAmount || 0,
        advance_release_use_fixed: formData.deductions.advanceRelease.useFixedAmount,
        advance_release_fixed_amount: parseFloat(formData.deductions.advanceRelease.fixedAmount || 0),
        advance_release_use_stored: formData.deductions.advanceRelease.useStoredAmount,

        retention_release_checked: formData.deductions.retentionRelease.checked,
        retention_release_percentage: formData.deductions.retentionRelease.checked ?
          parseInt(formData.deductions.retentionRelease.percentage) : 0,
        retention_release_taxable: deductionValues.retention?.taxable || 0,
        retention_release_vat: deductionValues.retention?.vatAmount || 0,
        retention_release_total: deductionValues.retention?.totalAmount || 0,

        cumulative_retention_checked: formData.deductions.cumulativeretention.checked,
        cumulative_retention_percentage: formData.deductions.cumulativeretention.checked ?
          parseInt(formData.deductions.cumulativeretention.percentage) : 0,
        cumulative_retention_taxable: deductionValues.cumulative?.taxable || 0,
        cumulative_retention_vat: deductionValues.cumulative?.vatAmount || 0,
        cumulative_retention_total: deductionValues.cumulative?.totalAmount || 0,

        cumulative_performance_retention_checked: formData.deductions.cumulativePerformanceRetention.checked,
        cumulative_performance_retention_percentage: formData.deductions.cumulativePerformanceRetention.checked ?
          parseInt(formData.deductions.cumulativePerformanceRetention.percentage) : 0,
        cumulative_performance_retention_taxable: deductionValues.performance?.taxable || 0,
        cumulative_performance_retention_vat: deductionValues.performance?.vatAmount || 0,
        cumulative_performance_retention_total: deductionValues.performance?.totalAmount || 0,

        ncr_deduction_checked: formData.deductions.ncrDeduction.checked,
        ncr_deduction_amount: deductionValues.ncr?.taxable || 0,
        ncr_deduction_vat: deductionValues.ncr?.vatAmount || 0,
        ncr_deduction_total: deductionValues.ncr?.totalAmount || 0,

        previous_payment_taxable: deductionValues.previous?.taxable || 0,
        previous_payment_vat: deductionValues.previous?.vatAmount || 0,
        previous_payment_total: deductionValues.previous?.totalAmount || 0,

        other_deduction_checked: formData.deductions.otherDeduction.checked,
        other_deduction_amount: deductionValues.other?.taxable || 0,
        other_deduction_vat: deductionValues.other?.vatAmount || 0,
        other_deduction_total: deductionValues.other?.totalAmount || 0,
        other_deduction_include_vat: formData.deductions.otherDeduction.checked ?
          formData.deductions.otherDeduction.includeVat : false,
        other_deduction_show_subtotal: formData.deductions.otherDeduction.checked ?
          formData.deductions.otherDeduction.showSubtotal : false,

        // Per-deduction Deduct/Add modes (retentionRelease is always 'add')
        deduction_modes: JSON.stringify(deductionModes),

        custom_row_order: JSON.stringify(customRowOrder),

        bank_name_branch: formData.bankDetails.bank === 'ADCB'
          ? 'ABU DHABI COMMERCIAL BANK PJSC, Dubai, UAE'
          : 'INVEST BANK PJSC, Dubai, UAE',
        account_number: formData.bankDetails.accountNumber,
        iban: formData.bankDetails.iban,
        swift: formData.bankDetails.swift,

        total_wd_last_period: parseFloat(formData.details.totalWDLastPeriod || 0),

        ...(isAliSons ? {
          landscape_preliminaries: parseFloat(formData.details.billItems?.landscape?.preliminaries?.amount || 0),
          landscape_works: parseFloat(formData.details.billItems?.landscape?.landscapingWorks?.amount || 0),
          tenderers_adjustments: parseFloat(formData.details.billItems?.landscape?.tenderersAdjustments?.amount || 0),
          provisional_sums: parseFloat(formData.details.billItems?.landscape?.ProvisionalSums?.amount || 0),
          swimming_preliminaries: parseFloat(formData.details.billItems?.swimming?.preliminaries?.amount || 0),
          swimming_mep: parseFloat(formData.details.billItems?.swimming?.mepWorks?.amount || 0),
          swimming_civil: parseFloat(formData.details.billItems?.swimming?.civilWorks?.amount || 0)
        } : {}),

        formatted_filename: generateFormattedFilename(
          formData.header.serialNumber,
          formData.header.taxInvoiceRef,
          client.client_name,
          project.project_name,
          formData.details.paymentCertificateNo,
          monthYear,
          project.project_code
        )
      };

      function generateFormattedFilename(serialNumber, taxInvoiceRef, clientName, projectName, paymentCertNo, monthYear, projectCode) {
        const cleanStr = (str) => str ? str.replace(/[\/\\:*?"<>|]/g, '-').trim() : '';

        const cleanSerialNumber = cleanStr(serialNumber);
        const cleanTaxInvoiceRef = cleanStr(taxInvoiceRef);
        const cleanClientName = cleanStr(clientName).split(',')[0];
        const cleanProjectName = cleanStr(projectName).split('@')[0].trim();
        const cleanPaymentCertNo = cleanStr(paymentCertNo);
        const cleanProjectCode = cleanStr(projectCode);

        return `Tax Invoice # ${cleanSerialNumber}, Inv # ${cleanTaxInvoiceRef}, ${cleanClientName}, ${cleanProjectName}, PC# ${cleanPaymentCertNo}, ${monthYear}, ${cleanProjectCode}.pdf`;
      }

      const result = await invoiceService.saveInvoiceWithTaxInvoicesList(pdfFile, metadata);

      if (result.status === 'success') {
        toast({
          title: "Success",
          description: result.is_update ?
            "Invoice updated successfully" :
            "Invoice created successfully",
          variant: "success"
        });
        onBack();
      } else {
        throw new Error(result.message || 'Failed to save invoice');
      }

    } catch (error) {
      console.error('Save invoice error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save invoice",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getFinalAmount = () => {
    const totalKey = 'total-totalAmount';
    if (manualAmounts[totalKey] !== undefined) {
      return Math.abs(manualAmounts[totalKey]);
    }
    return Math.abs(calculateFinalAmount(getOrderedRows()));
  };

  // Final amount = signed sum of all rows except the Sub Total display row.
  const calculateFinalAmount = (rows) => {
    if (!rows?.length) return 0;

    const manualTotalKey = 'total-totalAmount';
    if (manualAmounts[manualTotalKey] !== undefined) {
      return manualAmounts[manualTotalKey];
    }

    const getAdjustedRowAmount = (row, index) => {
      const manualKey = `${index}-totalAmount`;
      return manualAmounts[manualKey] !== undefined
        ? manualAmounts[manualKey]
        : (Number(row.totalAmount) || 0);
    };

    const total = rows.reduce((acc, row, index) => {
      if (row.isSubtotal) return acc;
      return acc + getAdjustedRowAmount(row, index);
    }, 0);

    return Number(total.toFixed(2));
  };

  const rows = calculateRows(formData);
  const orderedRows = getOrderedRows();
  const totals = calculateTotals(orderedRows);
  const finalAmount = calculateFinalAmount(orderedRows);

  return (
    <div className="min-h-screen bg-gray-50">
      <div
        id="invoice-content"
        ref={invoiceRef}
        className="w-[210mm] mx-auto bg-white shadow-lg"
        style={{
          minHeight: '297mm',
          padding: '25mm 10mm 25mm 8mm'
        }}
      >
        {/* Header Section */}
        <div className="mt-0">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-blue-500 text-xl font-bold underline">TAX INVOICE</div>
              <div className="text-red-600 font-bold border-b border-red-600 inline-block">
                S.N.: {formData.header?.serialNumber}...{new Date().getFullYear()}
              </div>
              <div className="font-bold leading-tight text-[11px]">Tax Invoice Ref: {formData.header?.taxInvoiceRef}</div>
              <div className="font-bold leading-tight text-[11px]">Tax Invoice Date: {formatDate(formData.header?.taxInvoiceDate)}</div>
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div className="relative mt-4">
          <div className="mb-2">
            <div className="flex items-start text-[11px] leading-tight">
              <span className="font-bold underline">From: </span>
              <span className="ml-1 font-bold">Phoenician Technical Services LLC.</span>
            </div>
            <div className="text-[11px] leading-tight font-bold">P.O Box: 377972, Dubai, UAE</div>
            <div className="text-[11px] leading-tight font-bold">Central Plaza, Office 228, DIP-1</div>
            <div className="text-[11px] leading-tight font-bold">Tel: 04-8829484 Fax: 04-8829585</div>
            <div className="relative inline-block">
              <span className="text-green-600 text-[11px] font-bold">TRN: 100034783900003</span>
              <div className="absolute bottom-0 left-0 w-full h-0.5 bg-green-600"></div>
            </div>
          </div>

          <div className="mb-2">
            <div className="flex items-start text-[11px] leading-tight">
              <span className="font-bold underline">To: </span>
              <span className="ml-1 font-bold">{client?.client_name}</span>
            </div>
            <div className="text-[11px] leading-tight font-bold">{client?.address}</div>
            <div className="text-[11px] leading-tight font-bold">Tel: {client?.telephone_no} FAX: {client?.fax_no}</div>
            <div className="relative inline-block">
              <span className="text-green-600 text-[11px] font-bold">TRN: {client?.trn}</span>
              <div className="absolute bottom-0 left-0 w-full h-0.5 bg-green-600"></div>
            </div>
          </div>

          <div className="absolute right-0 top-0 text-right text-blue-600 font-bold text-[11px] leading-tight">
            <div>{`Project: ${project?.project_name?.split('@')[0]}`}</div>
            <div>
              <span className="ml-28">{project?.project_name?.split('@')[1]}</span>
            </div>
            <div>{project?.loa_wo_sub_contract_ref?.split('dated')[0]}</div>
            <div>
              <span className="ml-28">{`dated ${project?.loa_wo_sub_contract_ref?.split('dated')[1]}`}</span>
            </div>
          </div>
        </div>

        {/* Contract Value */}
        <div className="text-blue-600 font-bold mb-4 text-[11px]">
          Contract Value: AED {formatNumber(project?.contract_value)}
        </div>

        {/* Table Section */}
        <div className="mb-4">
          <table className="w-full border-collapse border border-black [border-style:solid] [border-width:1px]">
            <thead>
              <tr className="h-7">
                <th className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-0.5 w-6 text-center text-[11px] font-bold">
                  S.NO
                </th>
                <th className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-center w-[30%] text-[11px] font-bold">
                  Description
                </th>
                <th className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-0.5 w-20 text-center text-[11px] font-bold">
                  Qty
                </th>
                <th className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 w-40 text-center text-[11px] font-bold">
                  Unit Price
                </th>
                <th className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 w-40 text-center text-[11px] font-bold">
                  Taxable
                </th>
                <th className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-0.5 w-14 text-center text-[11px] font-bold">
                  VAT %
                </th>
                <th className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 w-40 text-center text-[11px] font-bold">
                  VAT Amount
                </th>
                <th className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 w-44 text-center text-[11px] font-bold">
                  Total Amount<br />INCL VAT<br />(in AED)
                </th>
                <th className="border-none bg-transparent print-hidden w-12"></th>
              </tr>
            </thead>

            <tbody>
              {orderedRows.map((row, displayIndex) => {
                const getDescriptionColorClass = (row) => {
                  if (row.type === 'cumulative' && row.descriptionColor) {
                    switch (row.descriptionColor) {
                      case 'blue-600':
                        return 'text-blue-600';
                      case 'red-600':
                        return 'text-red-600';
                      case 'green-600':
                        return 'text-green-600';
                      default:
                        return 'text-black';
                    }
                  }
                  // Deductions -> red, everything else (incl. additions) -> black
                  return row.isDeduction ? 'text-red-600' : 'text-black';
                };

                return (
                  <tr key={`${row.type}-${displayIndex}`} className={`${row.isDeduction ? 'text-red-600' : ''} h-[22px]`}>
                    <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-0.5 text-center text-[11px] font-bold">
                      {displayIndex + 1}
                    </td>
                    <td className={`border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-[11px] font-bold ${getDescriptionColorClass(row)}`}
                      dangerouslySetInnerHTML={{ __html: row.description }}>
                    </td>
                    <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-0.5 text-center text-[11px] font-bold">
                      {row.qty}
                    </td>
                    <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-right text-[11px] font-bold">
                      <DecimalEditor value={row.unitPrice} identifier={displayIndex} field="unitPrice" />
                    </td>
                    <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-right text-[11px] font-bold">
                      <DecimalEditor value={row.taxable} identifier={displayIndex} field="taxable" />
                    </td>
                    <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-0.5 text-center text-[11px] font-bold">
                      {row.vatPercentage === 'NA' ? (
                        <div className="text-center">NA</div>
                      ) : (
                        <div className="text-center">{row.vatPercentage}%</div>
                      )}
                    </td>
                    <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-right text-[11px] font-bold">
                      <DecimalEditor value={row.vatAmount} identifier={displayIndex} field="vatAmount" />
                    </td>
                    <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-right text-[11px] font-bold">
                      <DecimalEditor value={row.totalAmount} identifier={displayIndex} field="totalAmount" />
                    </td>
                    <td className="border-none bg-transparent print-hidden">
                      <div className="row-controls flex items-center justify-center space-x-1">
                        <button
                          onClick={() => moveRow(displayIndex, 'up')}
                          disabled={displayIndex === 0}
                          className={`p-1 rounded ${displayIndex === 0 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100 cursor-pointer'}`}
                          title="Move Up"
                        >
                          <MoveUp size={16} />
                        </button>
                        <button
                          onClick={() => moveRow(displayIndex, 'down')}
                          disabled={displayIndex === orderedRows.length - 1}
                          className={`p-1 rounded ${displayIndex === orderedRows.length - 1 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100 cursor-pointer'}`}
                          title="Move Down"
                        >
                          <MoveDown size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {/* Total Row */}
              <tr className="h-[22px]">
                <td colSpan="4" className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-center font-bold text-[11px]">
                  Total Amount Due for Payment Including VAT (in AED)
                </td>
                <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-right font-bold text-[11px]">
                  <DecimalEditor value={totals.taxable} identifier="total" field="taxable" isTotal={true} />
                </td>
                <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-center font-bold text-[11px]">
                  5%
                </td>
                <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-right font-bold text-[11px]">
                  <DecimalEditor value={totals.vatAmount} identifier="total" field="vatAmount" isTotal={true} />
                </td>
                <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-right font-bold text-[11px]">
                  <DecimalEditor value={totals.totalAmount} identifier="total" field="totalAmount" isTotal={true} />
                </td>
                <td className="border-none bg-transparent print-hidden"></td>
              </tr>

              {/* Amount in Words Row */}
              <tr className="h-[22px]">
                <td colSpan="8" className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 font-bold text-center text-[11px]">
                  AED: {numberToWords(getFinalAmount())}
                </td>
                <td className="border-none bg-transparent print-hidden"></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Bank Details and Remarks */}
        <div className="mt-4" style={{ marginBottom: '25mm' }}>
          <div>
            <div className="font-bold underline mb-2 text-[11px]">Bank Details:</div>
            <div className="space-y-1">
              <div className="text-[11px] leading-tight font-bold">
                Account Name: {formData.bankDetails?.accountName}
              </div>
              <div className="text-[11px] leading-tight flex items-start">
                <span className="font-bold whitespace-nowrap">Bank Name & Branch:</span>
                <span className="bg-yellow-300 px-1 font-bold ml-1">
                  {formData.bankDetails?.bank === 'ADCB'
                    ? 'ABU DHABI COMMERCIAL BANK PJSC, Dubai, U.A.E.'
                    : 'INVEST BANK PJSC, Dubai, U.A.E.'}
                </span>
              </div>
              <div className="text-[11px] leading-tight font-bold">
                Account Number: {formData.bankDetails?.accountNumber}
              </div>
              <div className="text-[11px] leading-tight font-bold">
                IBAN: {formData.bankDetails?.iban}
              </div>
              <div className="text-[11px] leading-tight font-bold">
                SWIFT: {formData.bankDetails?.swift}
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="font-bold mb-1 text-[11px]">Remarks:</div>
            <div className="flex items-start">
              <span className="mr-1 font-bold">•</span>
              <div className="font-bold text-[11px] leading-tight flex flex-wrap">
                <span>If any questions concerning this Invoice, please contact Eng. Elie Harb, the General Manager, on </span>
                <span className="text-blue-600 mx-1">+971557877701</span>
                <span>, Email: </span>
                <span className="text-blue-600 inline-block">elie@phoenician-uae.com</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Control Buttons */}
      <div className="fixed bottom-4 right-4 space-x-2 print:hidden">
        <button
          onClick={onBack}
          className="px-4 py-2 bg-gray-100 border rounded hover:bg-gray-200 text-sm"
        >
          Edit
        </button>

        <button
          onClick={handleSaveInvoice}
          disabled={isSaving}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {isSaving ? 'Saving...' : 'Save Invoice'}
        </button>

        <button
          onClick={() => {
            const controls = document.querySelectorAll('.row-controls, .print-hidden');
            controls.forEach(el => el.style.display = 'none');

            if (window.confirm('Click OK, then in the print dialog:\n1. Choose "Save as PDF" as destination\n2. Click Save\n\nThis ensures perfect formatting like the preview.')) {
              setTimeout(() => {
                window.print();
                setTimeout(() => {
                  controls.forEach(el => el.style.display = '');
                }, 1000);
              }, 100);
            } else {
              controls.forEach(el => el.style.display = '');
            }
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          Download PDF
        </button>

        <button
          onClick={handlePrint}
          className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm flex items-center gap-2"
        >
          <Printer className="w-4 h-4" />
          Print Invoice
        </button>
      </div>

      {/* Loading Overlay */}
      {isGenerating && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 print:hidden">
          <div className="bg-white p-4 rounded-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm">Generating PDF... Please wait</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicePreview;